import { useAccessTokenRoles } from '@b-stock/fe-core/bstock-next/components'
import { type OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { SpinnerDots } from '@b-stock/fe-core/bstock-react'

import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { isBidBlockedOnShippingQuote } from '@/buyer-portal/components/features/listing/actions/auction/utils'

import { FormError } from '../ActionForm/FormError'
import { CenteredFormRow } from '../ActionForm/ui'

const ns = 'Listings.ListView.actionCell.auction'

const ShippingError = ({ listingId }: { listingId: string }) => (
  <FormError
    href={`/buy/listings/details/${listingId}`}
    messageId={`${ns}.error.shipping`}
  />
)

/**
 * Inline List-View gate for the bid cell. Shows a shipping-error link when a
 * quote is required but unavailable, a spinner while one generates, otherwise
 * the bid form. The quote is auto-generated by BidFormProvider; the Confirm Max
 * Bid modal renders its own loading/blocked states (shared via
 * isBidBlockedOnShippingQuote), so this only gates the inline cell.
 */
export const QuoteGate = ({
  children,
  listing,
}: {
  children: React.ReactNode
  listing: OrchestratedAuctionListingDetails
}) => {
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === 'ACTIVE'

  const {
    autoQuoteGeneration: { isLoading },
    selectedQuote,
  } = useBidFormContext()

  // Non-buyers never need a quote — render the bid flow without gating (and
  // without the loading spinner below).
  if (!isActiveBuyer) {
    return <>{children}</>
  }

  if (isLoading) {
    // A bare spinner is wider than the collapsed form slot, so it pushes the
    // action menu out of line with every other row.
    return (
      <CenteredFormRow>
        <SpinnerDots />
      </CenteredFormRow>
    )
  }

  if (
    isBidBlockedOnShippingQuote({
      isActiveBuyer,
      isSellerCollects: listing.sellerCollects === true,
      quoteLoading: isLoading,
      selectedQuote,
    })
  ) {
    return <ShippingError listingId={listing._id} />
  }

  // Quote is ready (or not needed), render children
  return <>{children}</>
}
